<template>
  <div class="install-page" style="font-family: Inter, sans-serif;">
  <div class="w-full max-w-[440px] mx-auto px-3 pt-10 pb-6">
    <button v-if="authStore.token" @click="$router.push('/profile')" class="mb-2 text-ink">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M15 18l-6-6 6-6"/></svg>
    </button>

    <!-- Логотип -->
    <div class="flex justify-center mb-3">
      <img src="/icon-apple.png" width="72" height="72" alt="Зоя" class="rounded-[16px] shadow-md" />
    </div>

    <h1 class="text-26 font-bold text-ink mb-1 text-center">Установка приложения</h1>
    <p class="text-16 text-ink mb-4 text-center">{{ isAndroid ? 'Скачайте Зою из RuStore или установите как веб-приложение' : 'Зою можно установить в качестве приложения на экран смартфона' }}</p>

    <div class="text-16 text-ink leading-relaxed">

      <!-- ══ Android: RuStore + сворачиваемый PWA ══ -->
      <div v-if="isAndroid">
        <!-- RuStore — главный блок -->
        <div class="bg-white rounded-2xl p-4 mb-3">
          <div class="flex items-center gap-3 mb-3">
            <img src="/icons/rustore.png" width="28" height="28" alt="RuStore" style="border-radius:6px" />
            <span class="font-bold text-18">RuStore</span>
          </div>
          <p class="text-15 text-ink leading-snug mb-4">
            Полноценное приложение с уведомлениями и автообновлениями.
          </p>
          <a
            href="https://www.rustore.ru/catalog/app/ru.zozhnik.zoya"
            target="_blank"
            rel="noopener"
            class="block w-full py-3.5 rounded-2xl bg-[#0077FF] text-white text-16 font-semibold text-center no-underline"
          >
            Скачать в RuStore
          </a>
        </div>

        <!-- APK — прямая загрузка -->
        <div class="bg-white rounded-2xl p-4 mb-3">
          <div class="flex items-center gap-3 mb-3">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#41291A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            <span class="font-bold text-18">Скачать .apk</span>
          </div>
          <p class="text-15 text-ink leading-snug mb-4">
            Прямая загрузка установочного файла без магазина приложений.
          </p>
          <a
            href="https://zozhnik.ru/apk/latest.apk"
            download
            class="block w-full py-3.5 rounded-2xl bg-ink text-cream text-16 font-semibold text-center no-underline"
          >
            Скачать .apk
          </a>
        </div>

        <!-- PWA — сворачиваемый блок -->
        <button
          @click="showPwa = !showPwa"
          class="w-full flex items-center justify-between bg-white/60 rounded-2xl px-4 py-3 text-14 text-ink/80 font-semibold"
        >
          <span>Или установить без магазина (PWA)</span>
          <svg :class="{ 'rotate-180': showPwa }" class="transition-transform" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 9l6 6 6-6"/></svg>
        </button>

        <div v-if="showPwa" class="mt-2">
          <!-- Chrome на Android -->
          <div v-if="browser === 'chrome'" class="bg-white rounded-2xl p-4">
            <div class="flex items-center gap-2 mb-3">
              <svg width="24" height="24" viewBox="0 0 48 48"><defs><linearGradient id="chr-a" x1="3.2173" y1="15" x2="44.7812" y2="15" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#d93025"/><stop offset="1" stop-color="#ea4335"/></linearGradient><linearGradient id="chr-b" x1="20.7219" y1="47.6791" x2="41.5039" y2="11.6837" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#fcc934"/><stop offset="1" stop-color="#fbbc04"/></linearGradient><linearGradient id="chr-c" x1="26.5981" y1="46.5015" x2="5.8161" y2="10.506" gradientUnits="userSpaceOnUse"><stop offset="0" stop-color="#1e8e3e"/><stop offset="1" stop-color="#34a853"/></linearGradient></defs><circle cx="24" cy="23.9947" r="12" fill="#fff"/><path d="M24,12H44.7812a23.9939,23.9939,0,0,0-41.5639.0029L13.6079,30l.0093-.0024A11.9852,11.9852,0,0,1,24,12Z" fill="url(#chr-a)"/><circle cx="24" cy="24" r="9.5" fill="#1a73e8"/><path d="M34.3913,30.0029,24.0007,48A23.994,23.994,0,0,0,44.78,12.0031H23.9989l-.0025.0093A11.985,11.985,0,0,1,34.3913,30.0029Z" fill="url(#chr-b)"/><path d="M13.6086,30.0031,3.218,12.006A23.994,23.994,0,0,0,24.0025,48L34.3931,30.0029l-.0067-.0068a11.9852,11.9852,0,0,1-20.7778.007Z" fill="url(#chr-c)"/></svg>
              <span class="font-bold text-17">Chrome</span>
            </div>
            <ol class="list-decimal pl-5 space-y-2">
              <li>Нажмите <strong>⋮</strong> (три точки) в правом верхнем углу</li>
              <li>Выберите <strong>«Установить приложение»</strong> или <strong>«Добавить на главный экран»</strong></li>
              <li>Подтвердите установку</li>
            </ol>
          </div>

          <!-- Яндекс Браузер на Android -->
          <div v-else-if="browser === 'yandex'" class="bg-white rounded-2xl p-4">
            <div class="flex items-center gap-2 mb-3">
              <img src="/icons/yandex-browser.svg" width="24" height="24" alt="Яндекс Браузер" />
              <span class="font-bold text-17">Яндекс Браузер</span>
            </div>
            <ol class="list-decimal pl-5 space-y-2">
              <li>Нажмите <strong>⋮</strong> (три точки) внизу экрана</li>
              <li>Выберите <strong>«Добавить на главный экран»</strong></li>
              <li>Подтвердите добавление</li>
            </ol>
          </div>

          <!-- Samsung Internet -->
          <div v-else-if="browser === 'samsung-internet'" class="bg-white rounded-2xl p-4">
            <div class="flex items-center gap-2 mb-3">
              <span class="font-bold text-17">Samsung Internet</span>
            </div>
            <ol class="list-decimal pl-5 space-y-2">
              <li>Нажмите <strong>☰</strong> (меню) внизу справа</li>
              <li>Выберите <strong>«Добавить страницу на»</strong> → <strong>«Главный экран»</strong></li>
              <li>Подтвердите добавление</li>
            </ol>
          </div>

          <!-- Fallback Android -->
          <div v-else class="bg-white rounded-2xl p-4">
            <ol class="list-decimal pl-5 space-y-2">
              <li>Откройте <strong>zoya.zozhnik.ru</strong> в Chrome</li>
              <li>Нажмите <strong>⋮</strong> → <strong>«Установить приложение»</strong></li>
              <li>Подтвердите установку</li>
            </ol>
          </div>
        </div>
      </div>

      <!-- ══ iOS: Safari / другой браузер (без изменений) ══ -->

      <!-- Safari на iOS -->
      <div v-else-if="browser === 'safari'">
        <div class="bg-white rounded-2xl p-4">
          <div class="flex items-center gap-2 mb-3">
            <img src="/icons/safari.svg" width="24" height="24" alt="Safari" />
            <span class="font-bold text-17">Safari</span>
          </div>
          <ol class="list-decimal pl-5 space-y-2">
            <li>Нажмите кнопку <strong>«Поделиться»</strong>
              <svg class="inline-block ml-1 -mt-0.5" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#006CFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
              внизу экрана
            </li>
            <li>Прокрутите вниз и выберите <strong>«На экран Домой»</strong></li>
            <li>Нажмите <strong>«Добавить»</strong> в правом верхнем углу</li>
          </ol>
        </div>
      </div>

      <!-- Chrome / другой браузер на iOS (установка невозможна) -->
      <div v-else-if="browser === 'ios-other'">
        <div class="bg-white rounded-2xl p-4">
          <p class="text-16 text-ink mb-3">На iOS установка приложения возможна только через <strong>Safari</strong>.</p>
          <p class="text-16 text-ink">Откройте <strong>zoya.zozhnik.ru</strong> в Safari, затем:</p>
          <ol class="list-decimal pl-5 space-y-2 mt-3">
            <li>Нажмите кнопку <strong>«Поделиться»</strong>
              <svg class="inline-block ml-1 -mt-0.5" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#006CFF" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
              внизу экрана
            </li>
            <li>Прокрутите вниз и выберите <strong>«На экран Домой»</strong></li>
            <li>Нажмите <strong>«Добавить»</strong></li>
          </ol>
        </div>
      </div>

      <!-- Другой браузер (десктоп или неизвестный) -->
      <div v-else>
        <div class="bg-white rounded-2xl p-4">
          <p class="text-16 text-ink">Откройте этот сайт на смартфоне в браузере Chrome (Android) или Safari (iPhone), чтобы установить приложение.</p>
        </div>
      </div>

    </div>

    <!-- Подсказки по устройству (только когда PWA секция раскрыта на Android) -->
    <div v-if="device && showPwa" class="mt-3">
      <div v-if="device === 'samsung'" class="bg-white/70 rounded-2xl p-4 text-13 text-ink/80 leading-snug">
        <p class="font-semibold text-ink mb-1">Samsung</p>
        <p>На Samsung иконка может появиться в списке всех приложений, а не на главном экране. Откройте App Drawer (свайп вверх), найдите «Зоя» и перетащите на главный экран.</p>
      </div>
      <div v-else-if="device === 'xiaomi'" class="bg-white/70 rounded-2xl p-4 text-13 text-ink/80 leading-snug">
        <p class="font-semibold text-ink mb-1">Xiaomi / Redmi / POCO</p>
        <p>MIUI может заблокировать добавление на главный экран. Если иконка не появилась:</p>
        <ol class="list-decimal pl-4 mt-1 space-y-0.5">
          <li>Настройки → Приложения → Разрешения → Ярлыки на рабочем столе → включите для Chrome</li>
          <li>Попробуйте установить ещё раз</li>
        </ol>
      </div>
      <div v-else-if="device === 'huawei'" class="bg-white/70 rounded-2xl p-4 text-13 text-ink/80 leading-snug">
        <p class="font-semibold text-ink mb-1">Huawei / Honor</p>
        <p>На Huawei без Google Play лучше всего скачать из RuStore.</p>
      </div>
    </div>

    <!-- Подсказка для неавторизованных -->
    <p v-if="!authStore.token" class="text-16 text-ink text-center px-2 mt-5" style="font-weight: 400;">
      Установили? Закройте браузер и откройте Зою с домашнего экрана
    </p>

    <!-- Кнопка прижата к низу -->
    <div v-if="!authStore.token" class="mt-5">
      <button
        @click="continueInBrowser"
        class="w-full py-3 rounded-2xl bg-ink text-cream text-15 font-semibold"
      >
        Пропустить и продолжить в браузере
      </button>
    </div>
  </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useAuthStore } from '../store/auth.js'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const router = useRouter()
const showPwa = ref(false)

function continueInBrowser() {
  router.push('/diary')
  authStore.openAuthPopup()
}

// Определяем конкретный браузер и устройство
const browser = ref('other')
const device = ref('') // samsung, xiaomi, huawei, realme, oppo
const ua = navigator.userAgent || ''
const isIOS = /iphone|ipad|ipod/i.test(ua)
const isAndroid = /android/i.test(ua)

// Детекция производителя Android
if (isAndroid) {
  if (/SamsungBrowser|SM-[A-Z]/i.test(ua)) device.value = 'samsung'
  else if (/Xiaomi|Redmi|POCO|Mi\s/i.test(ua)) device.value = 'xiaomi'
  else if (/HUAWEI|Honor/i.test(ua)) device.value = 'huawei'
  else if (/realme|RMX\d/i.test(ua)) device.value = 'realme'
  else if (/OPPO|CPH\d/i.test(ua)) device.value = 'oppo'
}

if (isIOS) {
  // На iOS: Safari = нет CriOS, нет FxiOS, нет YaBrowser
  if (/CriOS|FxiOS|YaBrowser|OPiOS/i.test(ua)) {
    browser.value = 'ios-other'
  } else {
    browser.value = 'safari'
  }
} else if (isAndroid) {
  if (/YaBrowser/i.test(ua)) {
    browser.value = 'yandex'
  } else if (/SamsungBrowser/i.test(ua)) {
    browser.value = 'samsung-internet'
  } else if (/Chrome/i.test(ua) && !/Edge|OPR/i.test(ua)) {
    browser.value = 'chrome'
  } else {
    browser.value = 'chrome' // fallback для Android — инструкция Chrome подойдёт
  }
}
</script>

<style scoped>
.install-page {
  position: fixed;
  inset: 0;
  background: #FAF3E8;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  z-index: 50;
}
</style>
